import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useResource } from "@/hooks/useResource";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, ErrorState, EmptyState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PlanBuilderDialog } from "@/components/production/PlanBuilderDialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { apiErrorMessage } from "@/lib/api";
import { fmtDate, fmtDateTime, fmtNumber } from "@/lib/format";
import { productTypeLabel, sectionBucket, planCode, explodeRecipe, fetchRecipe } from "@/lib/production";
import { toast } from "sonner";
import { Plus, Factory, Search, Trash2, RefreshCw, Loader2 } from "lucide-react";

const SECTIONS = ["All", "Bakery", "Other"];
// Plans that can be safely hard-deleted (projection only, no irreversible operational history).
const DELETABLE = ["Draft", "Planned"];

const isToday = (dt) => {
  if (!dt) return false;
  const d = new Date(dt);
  return !isNaN(d) && d.toDateString() === new Date().toDateString();
};
const sumLines = (plans, pick) =>
  plans.reduce((s, p) => s + (p.lines || []).reduce((ls, l) => ls + (Number(pick(l)) || 0), 0), 0);

export default function ProductionPlans() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useResource("/production-plans");
  const plans = useMemo(() => (Array.isArray(data) ? data : []), [data]);

  const today = useMemo(() => {
    const t = plans.filter((p) => isToday(p.date));
    const planned = sumLines(t, (l) => l.plannedQty ?? l.qty ?? l.quantity);
    const produced = sumLines(t, (l) => l.actualQty ?? l.producedQty ?? l.produced ?? l.fgQty);
    const rawIds = new Set();
    let issued = 0;
    t.forEach((p) => {
      (p.requirements || p.rawMaterials || []).forEach((r) => rawIds.add(r.ingredientId || r.id || r.name));
      (p.lines || []).forEach((l) => { if (l.reserved || l.issued || l.stockReserved) issued += 1; });
      if (p.stockReserved) issued += (p.lines || []).length;
    });
    return { planned, produced, variance: produced - planned, rawMaterials: rawIds.size, issued };
  }, [plans]);

  const [builderOpen, setBuilderOpen] = useState(false);
  const [section, setSection] = useState("All");
  const [q, setQ] = useState("");

  // Handoff from Order Consolidation: prefill the existing plan builder (user still reviews & saves).
  const location = useLocation();
  const ocPrefill = location.state?.ocPrefill || null;
  const ocNote = location.state?.ocNote || "";
  const ocSourceOrders = location.state?.ocSourceOrders || [];
  useEffect(() => {
    if (location.state?.ocPrefill) {
      setBuilderOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [activePrefill] = useState(ocPrefill);
  const [activeNote] = useState(ocNote);
  const [activeSourceOrders] = useState(ocSourceOrders);

  const filtered = useMemo(() => {
    return plans
      .filter((p) => section === "All" || sectionBucket(p.section) === sectionBucket(section))
      .filter((p) => {
        if (!q) return true;
        const hay = `${p.id} ${p.section} ${(p.lines || []).map((l) => l.productName).join(" ")}`.toLowerCase();
        return hay.includes(q.toLowerCase());
      })
      .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [plans, section, q]);

  const openPlan = (p) => navigate(`/production-plans/${p.id}`);
  const afterChange = () => { qc.invalidateQueries({ queryKey: ["resource", "/production-plans"] }); refetch(); };

  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [backfilling, setBackfilling] = useState(false);

  const canDelete = (p) => DELETABLE.includes(String(p?.status || "Draft"));
  const needsBackfill = useMemo(
    () => plans.filter((p) => (p.lines || []).length > 0 && (p.lines || []).every((l) => !(l.requirements || []).length)),
    [plans]
  );

  const doDelete = async () => {
    if (!toDelete) return;
    setDeleting(true);
    try {
      if (toDelete.stockReserved) {
        try { await api.post(`/production-plans/${toDelete.id}/release`, {}); } catch (_) { /* continue */ }
      }
      await api.delete(`/production-plans/${toDelete.id}`);
      toast.success(`Plan ${planCode(toDelete)} deleted`);
      setToDelete(null); afterChange();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Delete failed"));
    } finally { setDeleting(false); }
  };

  const recalcRequirements = async () => {
    if (!needsBackfill.length) return;
    setBackfilling(true);
    let done = 0, skipped = 0;
    try {
      for (const p of needsBackfill) {
        const updatedLines = [];
        let planSkipped = false;
        for (const l of p.lines || []) {
          if (!l.recipeId) { updatedLines.push(l); planSkipped = true; continue; }
          try {
            const rec = await fetchRecipe(l.recipeId);
            if (!rec) { updatedLines.push(l); planSkipped = true; continue; }
            // Respect the LOCKED version — only calculate if it matches the plan's locked version.
            const locked = String(l.recipeVersion ?? "").replace(/^v/i, "");
            const cur = String(rec?.version ?? "").replace(/^v/i, "");
            if (locked && cur && locked !== cur) { updatedLines.push(l); planSkipped = true; continue; }
            const reqs = explodeRecipe(rec, l.plannedQty);
            updatedLines.push({ ...l, requirements: reqs, packaging: rec?.packaging || l.packaging || [] });
          } catch (_) { updatedLines.push(l); planSkipped = true; }
        }
        if (updatedLines.some((l, i) => (l.requirements || []).length && !((p.lines || [])[i].requirements || []).length)) {
          await api.put(`/production-plans/${p.id}`, { date: p.date, section: p.section, status: p.status, lines: updatedLines, issued: p.issued || {} });
          done += 1;
        }
        if (planSkipped) skipped += 1;
      }
      toast.success(`Requirements recalculated for ${done} plan(s)${skipped ? ` · ${skipped} could not calculate (recipe required/version mismatch)` : ""}`);
      afterChange();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Recalculate failed"));
    } finally { setBackfilling(false); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Production Plan" subtitle="Plan · issue · produce">
        <Button data-testid="plan-new" className="gap-2 bg-amber-700 hover:bg-amber-800" onClick={() => setBuilderOpen(true)}>
          <Plus className="h-4 w-4" /> New Production Plan
        </Button>
      </PageHeader>

      <section>
        <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-stone-400">Today's Production</div>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {[
            ["plan-kpi-planned", "Planned Qty", fmtNumber(today.planned, 0)],
            ["plan-kpi-produced", "Produced Qty", fmtNumber(today.produced, 0)],
            ["plan-kpi-variance", "Variance", fmtNumber(today.variance, 0)],
            ["plan-kpi-rawmaterials", "Raw Materials", fmtNumber(today.rawMaterials, 0)],
            ["plan-kpi-issued", "Issued Lines", fmtNumber(today.issued, 0)],
          ].map(([tid, label, val]) => (
            <div key={tid} data-testid={tid} className="rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
              <div className="font-display text-2xl font-bold text-stone-900">{val}</div>
              <div className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-stone-500">{label}</div>
            </div>
          ))}
        </div>
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex gap-1.5">
          {SECTIONS.map((s) => (
            <Button key={s} size="sm" variant={section === s ? "default" : "outline"}
              data-testid={`plan-filter-${s.toLowerCase().replace(/[\s-]/g, "")}`}
              className={section === s ? "h-9 bg-stone-800 hover:bg-stone-900" : "h-9"} onClick={() => setSection(s)}>
              {s}
            </Button>
          ))}
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
          <Input data-testid="plan-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plans / products…" className="h-9 pl-8 text-sm" />
        </div>
      </div>

      {needsBackfill.length > 0 && (
        <div data-testid="plan-backfill-banner" className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="text-sm text-amber-800">
            <strong>{needsBackfill.length}</strong> plan(s) have an empty requirement table. Recalculate using each plan's <strong>locked</strong> recipe version (no stock movement).
          </div>
          <Button data-testid="plan-recalc-requirements" size="sm" className="h-9 gap-1.5 bg-amber-700 hover:bg-amber-800" disabled={backfilling} onClick={recalcRequirements}>
            {backfilling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Recalculate Requirements
          </Button>
        </div>
      )}

      {isLoading ? <LoadingState label="Loading plans…" /> :
        isError ? <ErrorState message={apiErrorMessage(error)} onRetry={refetch} /> :
        filtered.length === 0 ? <EmptyState title="No production plans" message="Create a plan to calculate ingredient requirements from an approved recipe." /> : (
          <>
            {/* Desktop table */}
            <div className="hidden overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm md:block">
              <table className="w-full text-sm">
                <thead className="border-b border-stone-200 bg-stone-50 text-left text-[11px] font-bold uppercase tracking-wider text-stone-500">
                  <tr>
                    <th className="px-4 py-2.5">Plan</th><th className="px-4 py-2.5">Section</th>
                    <th className="px-4 py-2.5">Products</th><th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Reserved</th><th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Updated</th><th className="px-4 py-2.5 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filtered.map((p) => (
                    <tr key={p.id} data-testid={`plan-row-${p.id}`} className="cursor-pointer hover:bg-amber-50/40" onClick={() => openPlan(p)}>
                      <td className="px-4 py-2.5"><span className="flex items-center gap-2 font-mono text-xs text-stone-700"><Factory className="h-3.5 w-3.5 text-amber-700" />{planCode(p)}</span></td>
                      <td className="px-4 py-2.5">{productTypeLabel(p.section) || p.section}</td>
                      <td className="px-4 py-2.5 text-stone-600">{(p.lines || []).length ? (p.lines || []).map((l) => l.productName).filter(Boolean).slice(0, 2).join(", ") + ((p.lines || []).length > 2 ? "…" : "") : <span className="text-stone-400">— empty —</span>}</td>
                      <td className="px-4 py-2.5"><StatusBadge value={p.status} /></td>
                      <td className="px-4 py-2.5">{p.stockReserved ? <span className="text-emerald-600">Yes</span> : <span className="text-stone-400">No</span>}</td>
                      <td className="px-4 py-2.5">{fmtDate(p.date)}</td>
                      <td className="px-4 py-2.5 text-xs text-stone-500">{fmtDateTime(p.updatedAt)}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Button data-testid={`plan-delete-${p.id}`} size="icon" variant="ghost"
                          className="h-8 w-8 text-stone-400 hover:text-rose-600 disabled:opacity-30"
                          disabled={!canDelete(p)} title={canDelete(p) ? "Delete plan" : "Cannot delete (has production history)"}
                          onClick={(e) => { e.stopPropagation(); setToDelete(p); }}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="space-y-3 md:hidden">
              {filtered.map((p) => (
                <button key={p.id} data-testid={`plan-card-${p.id}`} onClick={() => openPlan(p)}
                  className="w-full rounded-xl border border-stone-200 bg-white p-4 text-left shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs text-stone-600">{planCode(p)}</span>
                    <StatusBadge value={p.status} />
                  </div>
                  <div className="mt-1 text-sm font-medium text-stone-800">{productTypeLabel(p.section) || p.section} · {fmtDate(p.date)}</div>
                  <div className="mt-0.5 text-xs text-stone-500">{(p.lines || []).length} product line(s){p.stockReserved ? " · reserved" : ""}</div>
                </button>
              ))}
            </div>
          </>
        )}

      <PlanBuilderDialog open={builderOpen} onOpenChange={setBuilderOpen} onCreated={afterChange}
        prefill={activePrefill} prefillNote={activeNote} prefillSourceOrders={activeSourceOrders} />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Production Plan?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-1 text-sm text-stone-600">
                {toDelete && (<>
                  <div>Plan Number: <strong>{planCode(toDelete)}</strong></div>
                  <div>Production Date: <strong>{fmtDate(toDelete.date)}</strong></div>
                  <div>Product: <strong>{(toDelete.lines || []).map((l) => l.productName).filter(Boolean).join(", ") || "— none —"}</strong></div>
                  <div>Planned Quantity: <strong>{fmtNumber((toDelete.lines || []).reduce((s, l) => s + (Number(l.plannedQty) || 0), 0))}</strong></div>
                  <div>Status: <strong>{toDelete.status}</strong></div>
                  {toDelete.stockReserved && <div className="mt-1 rounded bg-amber-50 px-2 py-1 text-xs text-amber-700">Reserved stock will be released before deletion.</div>}
                </>)}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="plan-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="plan-delete-confirm" className="bg-rose-600 hover:bg-rose-700" disabled={deleting} onClick={doDelete}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete Plan"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
